import { CAPABILITIES, GRANT_ONLY_CAPABILITIES, ROLES, ROLE_LABEL, api, type Capability, type Role } from '@agency/shared';
import { Button, Checkbox, Dialog, Field, Input } from '@agency/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { SensitiveButton } from '../../app/widgets.js';
import { useMutation } from '../../lib/mutate.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: readonly api.adminAccounts.AccountRegistryEntry[];
}

export function InviteDialog({ open, onOpenChange, accounts }: Props) {
  const mutation = useMutation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roles, setRoles] = useState<Role[]>(['forward_deployed_operator']);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [grants, setGrants] = useState<Capability[]>([]);
  const [touched, setTouched] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const valid = emailValid && roles.length > 0;

  const reset = () => {
    setEmail('');
    setDisplayName('');
    setRoles(['forward_deployed_operator']);
    setAccountIds([]);
    setGrants([]);
    setTouched(false);
  };

  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    const result = await mutation.run(
      'admin.people.invite',
      // real-providers change: no direct-send switch; Agency creates Gmail drafts only.
      { input: { email, ...(displayName ? { displayName } : {}), roles, accountIds: accountIds as api.User['accountIds'], capabilityGrants: grants } },
      { title: (d) => `Invited ${d.user.displayName}`, invalidate: ['admin.people', 'admin.invitations', 'admin.overview'] },
    );
    if (result) {
      onOpenChange(false);
      reset();
      void navigate({ to: '/people/$userId', params: { userId: result.user.id } });
    }
  };

  const toggle = <T,>(list: T[], value: T): T[] => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  const grantable = CAPABILITIES.filter((c) => c === 'account.register' || GRANT_ONLY_CAPABILITIES.includes(c));

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
      title="Invite person"
      subtitle="Single-use token · expires in 7 days"
      width={560}
      actions={
        <>
          <Button variant="tertiary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <SensitiveButton variant="primary" loading={mutation.busy} onClick={() => void submit()}>Send invitation</SensitiveButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Email" required error={touched && !emailValid ? 'Enter the work email the identity provider will assert.' : undefined}>
          <Input mono type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@getagency.com" autoFocus />
        </Field>
        <Field label="Display name" hint="OPTIONAL">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Shown in the console and the desktop" />
        </Field>
        <Field label="Roles" required error={touched && roles.length === 0 ? 'Choose at least one role.' : undefined}>
          <div className="grid grid-cols-2 gap-2">
            {ROLES.map((r) => (
              <Checkbox key={r} label={ROLE_LABEL[r]} checked={roles.includes(r)} onCheckedChange={() => setRoles((prev) => toggle(prev, r))} />
            ))}
          </div>
        </Field>
        <Field label="Expected accounts" hint={`${accountIds.length} SELECTED`} help="Informational. Accounts are added on the desktop; nothing enforces this list.">
          {accounts.length === 0 ? (
            <p className="text-xs text-mute">No registry accounts yet.</p>
          ) : (
            <div className="scrollbar-thin grid max-h-40 grid-cols-2 gap-1 overflow-y-auto rounded-sm border border-line bg-bg p-2">
              {accounts.filter((a) => a.lifecycle === 'active').map((a) => (
                <Checkbox key={a.id} label={a.displayName} description={a.provider.toUpperCase()} checked={accountIds.includes(a.id)} onCheckedChange={() => setAccountIds((prev) => toggle(prev, a.id))} />
              ))}
            </div>
          )}
        </Field>
        <Field label="Explicit grants" hint="OPTIONAL">
          <div className="flex flex-wrap gap-3">
            {grantable.map((c) => (
              <Checkbox key={c} label={<span className="font-mono text-xs">{c}</span>} checked={grants.includes(c)} onCheckedChange={() => setGrants((prev) => toggle(prev, c))} />
            ))}
          </div>
        </Field>
      </div>
    </Dialog>
  );
}
